import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '@/components/ui/Icon';
import { StatusDot } from '@/components/ui/StatusDot';
import { EditorialImage } from '@/components/editorial/EditorialImage';
import { FeedCard, type CardFace } from '@/components/feed/FeedCard';
import type { FeedItem } from '@/modules/feed/composer';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/cn';

const EASE = [0.19, 1, 0.22, 1] as const;

interface FeedItemCardProps {
  item: FeedItem;
  index: number;
  /** For .app profile cards only — propagates to FeedCard. */
  defaultFace?: CardFace;
}

/**
 * Single feed item dispatcher.
 *
 *   .app profile → existing FeedCard (stats/photo flip)
 *   .vip profile → cross-promo card with .vip badge, B&W, routes to VIP profile
 *   .tv series   → cross-promo card with .tv badge, full-bleed still, routes to series detail
 *   .tv creator  → portrait-led .tv badge card, routes to creator channel
 *
 * Cross-promo items wear a small `★ HOTTR.vip` / `★ HOTTR.tv` chip top-left
 * so the user knows the tap takes them to another surface.
 */
export const FeedItemCard = ({ item, index, defaultFace }: FeedItemCardProps) => {
  if (item.kind === 'app_profile') {
    return <FeedCard profile={item.profile} index={index} defaultFace={defaultFace} />;
  }
  if (item.kind === 'vip_profile') return <VipPromoCard item={item} index={index} />;
  if (item.kind === 'tv_series') return <TvSeriesPromoCard item={item} index={index} />;
  if (item.kind === 'tv_creator') return <TvCreatorPromoCard item={item} index={index} />;
  return null;
};

/* ============================================================
   VIP CROSS-PROMO CARD
   B&W treatment to align with VIP's gazette aesthetic.
   ============================================================ */

const VipPromoCard = ({
  item,
  index,
}: {
  item: Extract<FeedItem, { kind: 'vip_profile' }>;
  index: number;
}) => {
  const { profile } = item;
  const heroPhoto = profile.photos[0];

  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE, delay: Math.min(index, 6) * 0.04 }}
      className="relative isolate aspect-[4/5]"
    >
      <Link
        to={ROUTES.vip.profile(profile.handle)}
        className={cn(
          'group relative block h-full overflow-hidden',
          'border border-pink/20 transition-[border-color] duration-base ease-signal',
          'hover:border-pink/60',
        )}
      >
        <EditorialImage
          scene={profile.scene}
          seed={profile.sceneSeed}
          photoUrl={heroPhoto}
          aspect="aspect-auto"
          className="h-full"
          mono
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(5,6,8,0.30) 0%, rgba(5,6,8,0) 25%, rgba(5,6,8,0.30) 55%, rgba(5,6,8,0.95) 100%)',
          }}
        />

        {/* Surface badge top-left */}
        <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 border border-pink/40 bg-ink/85 px-2.5 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur shadow-glow-soft">
          ★ HOTTR<span className="text-pink">.</span>
          <span className="font-light italic normal-case">vip</span>
        </span>

        {/* Verified */}
        {profile.verification === 'verified' && (
          <span className="absolute right-4 top-4 inline-flex items-center gap-1 border border-pink/40 bg-ink/85 px-2 py-0.5 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur">
            <Icon name="verified" filled size={11} />
            Verified
          </span>
        )}

        {/* Footer */}
        <footer className="absolute inset-x-0 bottom-0 p-5 md:p-6">
          <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            <span className="text-pink">No.</span> {String(profile.memberNo).padStart(3, '0')}
            <span className="mx-2 text-pink">·</span>
            {profile.suburb} · {profile.city}
          </p>
          <h3
            className="mt-1.5 font-display font-black uppercase leading-[0.92] tracking-display"
            style={{ fontSize: 'clamp(28px, 3.6vw, 44px)', letterSpacing: '-0.04em' }}
          >
            {profile.displayName}<span className="text-pink">.</span>
          </h3>
          <p className="mt-2 line-clamp-1 text-sm italic text-text-secondary md:text-[15px]">
            &ldquo;{profile.mood}&rdquo;
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 font-display text-[9px] font-bold uppercase tracking-widest text-pink">
            <Icon name="arrow_forward" size={11} />
            Open on the directory
          </p>
        </footer>
      </Link>
    </motion.article>
  );
};

/* ============================================================
   TV SERIES CROSS-PROMO CARD
   ============================================================ */

const TvSeriesPromoCard = ({
  item,
  index,
}: {
  item: Extract<FeedItem, { kind: 'tv_series' }>;
  index: number;
}) => {
  const { series } = item;
  const totalEps = series.episodes.length;

  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE, delay: Math.min(index, 6) * 0.04 }}
      className="relative isolate aspect-[4/5]"
    >
      <Link
        to={ROUTES.tv.series(series.slug)}
        className={cn(
          'group relative block h-full overflow-hidden',
          'border border-white/[0.10] transition-[border-color] duration-base ease-signal',
          'hover:border-pink/40',
        )}
      >
        <EditorialImage
          scene={series.scene}
          seed={series.sceneSeed}
          photoUrl={series.heroUrl}
          aspect="aspect-auto"
          className="h-full"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(5,6,8,0.20) 0%, rgba(5,6,8,0) 30%, rgba(5,6,8,0.45) 65%, rgba(5,6,8,0.95) 100%)',
          }}
        />

        <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 border border-pink/40 bg-ink/85 px-2.5 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur shadow-glow-soft">
          ★ HOTTR<span className="text-pink">.</span>
          <span className="font-light italic normal-case">tv</span>
        </span>

        {/* Episode meta top-right */}
        <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 border border-white/[0.10] bg-ink/85 px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-text-secondary backdrop-blur">
          {totalEps} ep
          <span aria-hidden className="text-pink">·</span>
          {series.rating}
        </span>

        <footer className="absolute inset-x-0 bottom-0 p-5 md:p-6">
          <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            {series.eyebrow}
          </p>
          <h3
            className="mt-1.5 font-display font-black uppercase leading-[0.92] tracking-display"
            style={{ fontSize: 'clamp(30px, 3.8vw, 48px)', letterSpacing: '-0.05em' }}
          >
            {series.title}<span className="text-pink">.</span>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm text-text-secondary md:text-[15px]">
            <span className="text-text-primary">{series.tagline}</span>
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 font-display text-[9px] font-bold uppercase tracking-widest text-pink">
            <Icon name="play_arrow" size={11} filled />
            Watch on HOTTR<span className="text-pink">.</span>tv
          </p>
        </footer>
      </Link>
    </motion.article>
  );
};

/* ============================================================
   TV CREATOR CROSS-PROMO CARD
   ============================================================ */

const TvCreatorPromoCard = ({
  item,
  index,
}: {
  item: Extract<FeedItem, { kind: 'tv_creator' }>;
  index: number;
}) => {
  const { creator } = item;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE, delay: Math.min(index, 6) * 0.04 }}
      className="relative isolate aspect-[4/5]"
    >
      <Link
        to={ROUTES.tv.creator(creator.handle)}
        className={cn(
          'group relative block h-full overflow-hidden',
          'border border-white/[0.10] transition-[border-color] duration-base ease-signal',
          'hover:border-pink/40',
        )}
      >
        <img
          src={creator.portraitUrl}
          alt=""
          loading="lazy"
          className="hottr-matte-editorial absolute inset-0 h-full w-full object-cover"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(5,6,8,0.25) 0%, rgba(5,6,8,0) 30%, rgba(5,6,8,0.50) 65%, rgba(5,6,8,0.95) 100%)',
          }}
        />

        <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 border border-pink/40 bg-ink/85 px-2.5 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur shadow-glow-soft">
          ★ HOTTR<span className="text-pink">.</span>
          <span className="font-light italic normal-case">tv</span>
        </span>

        {creator.isVerified && (
          <span className="absolute right-4 top-4 inline-flex items-center gap-1 border border-pink/40 bg-ink/85 px-2 py-0.5 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur">
            <Icon name="verified" filled size={11} />
            Verified
          </span>
        )}

        <footer className="absolute inset-x-0 bottom-0 p-5 md:p-6">
          <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Creator · {creator.role}
            <span className="mx-2 text-pink">·</span>
            {creator.subscribers >= 1000
              ? `${(creator.subscribers / 1000).toFixed(1)}k subs`
              : `${creator.subscribers} subs`}
          </p>
          <h3
            className="mt-1.5 font-display font-black uppercase leading-[0.92] tracking-display"
            style={{ fontSize: 'clamp(26px, 3.4vw, 40px)', letterSpacing: '-0.04em' }}
          >
            {creator.name}<span className="text-pink">.</span>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm text-text-secondary md:text-[15px]">
            {creator.bio}
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 font-display text-[9px] font-bold uppercase tracking-widest text-pink">
            <Icon name="arrow_forward" size={11} />
            Channel on HOTTR<span className="text-pink">.</span>tv
          </p>
        </footer>
      </Link>
    </motion.article>
  );
};

// re-export the type so consumers don't need to know about the composer
export type { FeedItem };

// Tiny live presence badge — exported for the mobile swipe feed.
export const LiveBadge = ({ on }: { on: boolean }) =>
  on ? (
    <span className="inline-flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
      <StatusDot status="live" size="h-1.5 w-1.5" />
      Live
    </span>
  ) : null;
